import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import WalkthroughButton from '../WalkthroughButton';
import ZeroDTETourController from './ZeroDTETourController';
import {
  runScreenerBackend,
  getSavedParameters,
  saveParameterSet,
  deleteParameterSet
} from '../../services/api';
import { isUsageLimitError, getUsageLimitData, isFeatureAccessError, getFeatureAccessData } from '../../utils/usageLimitHelper';
import { isIntradayShapedParam, rehydrateIntradayParam, packIntradayExtra } from '../../utils/presetMappings';
import UsageLimitModal from '../UsageLimitModal';
import FeatureAccessModal from '../FeatureAccessModal';
import ZeroDTEForm from './ZeroDTEForm';
import MobileZeroDTEForm from './MobileZeroDTEForm';
import UnderlyingChartCard from './UnderlyingChartCard';
import ScreenerSavedParams from '../ScreenerSavedParams';
import ScreenerResponsive from '../Screener/ScreenerResponsive';
import ScreenerQueue from '../ScreenerQueue';
import StandardLoader from '../StandardLoader';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// 0DTE module page. Structurally the Screener page minus the mode toggle: one
// ticker, short-dated expiration bands, and the same queue / preset / results
// machinery. Results render through ScreenerResponsive with screenerMode
// 'intraday' — that value is the results layer's name for short-dated
// semantics (fractional DTE, sub-day exits, fine-grained scenarios), and this
// module is now its only producer.
const RESULTS_CACHE_KEY = 'zerodte_last_results';
const RESULTS_CACHE_TS_KEY = 'zerodte_last_results_timestamp';

function ZeroDTEPage() {
  const [paramSets, setParamSets] = useState([]);  // Start with empty queue
  // Counter bumped on every explicit "+ Add to Queue" — tells ScreenerQueue to
  // auto-expand. Direct runs never bump it, so the queue card stays collapsed.
  const [queueExpandSignal, setQueueExpandSignal] = useState(0);
  const [savedParams, setSavedParams] = useState([]);
  const [results, setResults] = useState(null);
  const [lastRunParamSets, setLastRunParamSets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState(null);
  // Pulse-trigger that fires the auto-scroll to the results section after a
  // successful run (from either the form's "Run" CTA or the queue's "Run").
  const [scrollResultsTick, setScrollResultsTick] = useState(0);
  const resultsAnchorRef = useRef(null);

  const formRef = useRef(null);
  const leftPanelRef = useRef(null);

  // Mirror of the form's ticker + target-price context, used by the chart card
  // in the right panel. The form owns this state; it reports changes upward.
  const [targetCtx, setTargetCtx] = useState({
    ticker: null,
    strikeMode: 'bands',
    targetPrice: '',
    bufferPct: null,
    currentPrice: null,
  });
  // Bumped when a NEW ticker is chosen: collapses Saved Parameters and expands
  // the chart card, once per selection. Both consumers react to the change, so
  // the user stays in control of each card afterwards.
  const [tickerSelectSignal, setTickerSelectSignal] = useState(0);
  const prevTickerRef = useRef(null);

  const isMobile = useMediaQuery('(max-width: 768px)');

  // Identity must be stable — ZeroDTEForm's emit effect depends on it.
  const handleTargetContextChange = useCallback((ctx) => {
    if (ctx.ticker && ctx.ticker !== prevTickerRef.current) {
      setTickerSelectSignal(s => s + 1);
    }
    prevTickerRef.current = ctx.ticker;
    setTargetCtx(ctx);
  }, []);

  const handlePickTargetPrice = useCallback((price) => {
    formRef.current?.setTargetFromChart?.(price);
  }, []);

  // Only 0DTE-shaped presets belong in this module's list; the Screener page
  // shows the complement. Both read the one shared saved-parameter store.
  const zeroDteParams = useMemo(
    () => savedParams.filter(isIntradayShapedParam),
    [savedParams]
  );

  // Single underlying price for the whole result set — enables the price shown
  // beside every scenario % column. Null whenever the results somehow span more
  // than one ticker, which switches those labels back to plain percentages.
  const scenarioBasePrice = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) return null;
    const tickers = new Set(results.map(r => r && r['Ticker']).filter(Boolean));
    if (tickers.size !== 1) return null;
    const price = parseFloat(results[0]['Underlying Price']);
    return Number.isFinite(price) && price > 0 ? price : null;
  }, [results]);

  // Restore cached results on mount
  useEffect(() => {
    fetchSavedParams();

    try {
      const cachedResults = localStorage.getItem(RESULTS_CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(RESULTS_CACHE_TS_KEY);

      if (cachedResults && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp);
        const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);

        // Only restore if less than 24 hours old
        if (hoursSinceCache < 24) {
          const parsed = JSON.parse(cachedResults);
          setResults(parsed);
          console.log(`✅ Restored ${parsed.length || 0} 0DTE results from cache (${Math.round(hoursSinceCache * 60)} minutes old)`);
        } else {
          localStorage.removeItem(RESULTS_CACHE_KEY);
          localStorage.removeItem(RESULTS_CACHE_TS_KEY);
          console.log('Cleared stale 0DTE cache (>24hrs old)');
        }
      }
    } catch (err) {
      console.error('Error restoring 0DTE cache:', err);
    }

    // eslint-disable-next-line
  }, []);

  // Measure left panel height for saved params scrollable area
  useEffect(() => {
    const measureLeftPanel = () => {
      if (leftPanelRef.current && !isMobile) {
        setLeftPanelHeight(leftPanelRef.current.offsetHeight);
      }
    };

    measureLeftPanel();
    window.addEventListener('resize', measureLeftPanel);

    const resizeObserver = new ResizeObserver(measureLeftPanel);
    if (leftPanelRef.current) {
      resizeObserver.observe(leftPanelRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureLeftPanel);
      resizeObserver.disconnect();
    };
  }, [isMobile]);

  // After a fresh run, jump to the top of the results section. Skipped on
  // mount-from-cache (scrollResultsTick stays 0 until a run fires).
  useEffect(() => {
    if (scrollResultsTick === 0) return;
    if (!resultsAnchorRef.current) return;
    const raf = requestAnimationFrame(() => {
      try {
        resultsAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        resultsAnchorRef.current.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollResultsTick]);

  const fetchSavedParams = async () => {
    try {
      const res = await getSavedParameters();
      setSavedParams(res.data);
    } catch (err) {
      console.error('Error fetching saved parameters:', err);
    }
  };

  const runScreenerWithParams = async (paramsToRun) => {
    if (!Array.isArray(paramsToRun) || paramsToRun.length === 0) return;
    console.log(`🎯 0DTE run with ${paramsToRun.length} scenario(s).`);

    setLoading(true);
    setError(null);

    try {
      const formatted = paramsToRun.map((p) => ({
        ...p,
        tickers: Array.isArray(p.tickers)
          ? p.tickers
          : p.tickers.split(',').map((t) => t.trim().toUpperCase()),
        strike_pct: parseFloat(p.strike_pct),
        stock_gain_pct: parseFloat(p.stock_gain_pct),
        allocation: parseFloat(p.allocation),
        days_to_gain: parseInt(p.days_to_gain),
        days_until_exp: parseInt(p.days_until_exp),
      }));

      const res = await runScreenerBackend({ param_sets: formatted });
      setResults(res.data);
      setLastRunParamSets(formatted);
      setScrollResultsTick((t) => t + 1);

      try {
        localStorage.setItem(RESULTS_CACHE_KEY, JSON.stringify(res.data));
        localStorage.setItem(RESULTS_CACHE_TS_KEY, Date.now().toString());
      } catch (err) {
        console.error('Error caching 0DTE results:', err);
      }
    } catch (err) {
      console.error(err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else if (isFeatureAccessError(err)) {
        setFeatureAccessData(getFeatureAccessData(err));
      } else {
        setError('Failed to run 0DTE screener.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    await runScreenerWithParams(paramSets);
  };

  // Form's "Run X Scenarios" CTA: append the form's scenarios to the queue
  // AND run immediately so the queue reflects what was executed.
  const handleAddAndRun = async (scenariosToAdd) => {
    if (!Array.isArray(scenariosToAdd) || scenariosToAdd.length === 0) return;
    const next = [...paramSets, ...scenariosToAdd];
    setParamSets(next);
    await runScreenerWithParams(next);
  };

  const deleteSavedScenario = async (id) => {
    await deleteParameterSet(id);
    fetchSavedParams();
  };

  const saveScenario = async (param) => {
    // The eight base SavedScreenerParameter columns cannot hold this module's
    // expiration window, strike band, or target-price metadata. Pack them into
    // `extra` so the preset re-runs the exact scenario it was saved from rather
    // than degrading to the engine's legacy tier window on reload.
    const payload = {
      ...param,
      extra: packIntradayExtra(param),
      tickers: Array.isArray(param.tickers)
        ? param.tickers.map((t) => t.trim().toUpperCase())
        : param.tickers.split(',').map((t) => t.trim().toUpperCase()),
    };
    try {
      await saveParameterSet(payload);
      fetchSavedParams();
      return true;
    } catch (err) {
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else if (isFeatureAccessError(err)) {
        setFeatureAccessData(getFeatureAccessData(err));
      } else {
        console.error('Error saving parameter:', err);
        setError('Failed to save parameter.');
      }
      return false;
    }
  };

  // Queue management
  const addToQueue = (scenario) => {
    setParamSets(prev => [...prev, scenario]);
    setQueueExpandSignal(s => s + 1);
  };

  // A saved preset loses its expiration window and strike range in the database,
  // so restore them before it enters the queue — otherwise the backend runs its
  // legacy ±30d tier path and returns different contracts than the preset built.
  const addParamToQueue = (param) => {
    addToQueue(rehydrateIntradayParam(param));
  };

  const removeFromQueue = (index) => {
    setParamSets(prev => prev.filter((_, i) => i !== index));
  };

  const clearQueue = () => {
    setParamSets([]);
  };

  const canRunScreener = () => paramSets.length > 0;

  const loadFromQueue = (index) => {
    const scenario = paramSets[index];
    if (!scenario || !formRef.current) return;
    formRef.current.loadScenario(scenario);
    removeFromQueue(index);
  };

  const loadFromSaved = (param) => {
    if (!formRef.current) return;
    formRef.current.loadScenario(rehydrateIntradayParam(param));
  };

  return (
    // `clip` instead of `hidden` on desktop: hidden computes overflow-y to auto,
    // which makes this div a scroll container and stops the right panel's chart
    // card from sticking to the viewport. `clip` is not a scroll container, so it
    // keeps the horizontal-overflow guard without breaking sticky.
    <div className="px-0 py-4 md:p-4 text-text" style={{ overflowX: isMobile ? 'hidden' : 'clip' }}>
      <div className="flex items-center gap-3 mb-6 px-4 md:px-0">
        <h2 className="text-2xl font-bold text-brand-white">0DTE Screener</h2>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            background: '#218aff',
            color: '#fff',
            borderRadius: 4,
            padding: '1px 5px',
            lineHeight: 1.4,
          }}
        >
          Beta
        </span>
        <WalkthroughButton sectionKey="0dte" />
        <ZeroDTETourController
          hasResults={Array.isArray(results) && results.length > 0}
          isMobile={isMobile}
          formRef={formRef}
        />
      </div>

      {/* Mobile Layout — pill-based single-screen form (presets via slide-up sheet) */}
      {isMobile ? (
        <form onSubmit={handleSubmit} className="mb-6">
          <MobileZeroDTEForm
            ref={formRef}
            onAddToQueue={addToQueue}
            onAddAndRun={handleAddAndRun}
            onSaveScenario={saveScenario}
            queueCount={paramSets.length}
            loading={loading}
            savedParams={zeroDteParams}
            onLoadParam={loadFromSaved}
            onDeleteParam={deleteSavedScenario}
            onAddParamToQueue={addParamToQueue}
          />
        </form>
      ) : (
        /* Desktop split-panel layout wrapper */
        <div className="screener-layout-wrapper">
          {/* Left Panel - Form */}
          <div className="screener-left-panel" ref={leftPanelRef}>
            <form onSubmit={handleSubmit} className="space-y-6 mb-6">
              <ZeroDTEForm
                ref={formRef}
                onAddToQueue={addToQueue}
                onAddAndRun={handleAddAndRun}
                onSaveScenario={saveScenario}
                queueCount={paramSets.length}
                loading={loading}
                onTargetContextChange={handleTargetContextChange}
              />
            </form>
          </div>

          {/* Right Panel - Saved Parameters + sticky price chart (desktop only) */}
          <div className="screener-right-panel">
            <ScreenerSavedParams
              savedParams={zeroDteParams}
              onLoad={loadFromSaved}
              onDelete={deleteSavedScenario}
              onAddToQueue={addParamToQueue}
              maxContentHeight={leftPanelHeight}
              collapseSignal={tickerSelectSignal}
            />
            <UnderlyingChartCard
              ticker={targetCtx.ticker}
              strikeMode={targetCtx.strikeMode}
              targetPrice={targetCtx.targetPrice}
              bufferPct={targetCtx.bufferPct}
              currentPrice={targetCtx.currentPrice}
              expandSignal={tickerSelectSignal}
              onPickTargetPrice={handlePickTargetPrice}
            />
          </div>
        </div>
      )}

      {/* Full-width Queue Section */}
      <div className="queue-section-fullwidth px-4 md:px-0">
        <ScreenerQueue
          scenarios={paramSets}
          expandSignal={queueExpandSignal}
          onRemove={removeFromQueue}
          onClear={clearQueue}
          onLoad={loadFromQueue}
          onSave={saveScenario}
          onSubmit={handleSubmit}
          canRun={canRunScreener()}
          loading={loading}
        />
      </div>

      <StandardLoader show={loading} />

      {error && <p className="text-error">{error}</p>}

      {results && Array.isArray(results) && results.length === 0 && (
        <div className="card bg-yellow-900 bg-opacity-20 border-yellow-600 mt-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">No Contracts Found</h3>
              <p className="text-gray-300 mb-4">
                No option contracts matched these parameters. For short-dated scans this usually means:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mb-4">
                <li>No expirations exist inside the selected window (e.g. no 0DTE listed for this ticker today)</li>
                <li>The strike band or target-price buffer is too narrow to contain a listed strike</li>
                <li>The ticker has no weekly options, or is invalid/delisted</li>
              </ul>
              <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded p-3">
                <p className="text-blue-300 font-semibold mb-2">Try These Adjustments:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200 text-sm">
                  <li>Widen the buffer around your target price (or switch to Exact target)</li>
                  <li>Select a longer expiration window (1–3 Weeks, 1–2 Months)</li>
                  <li>Use a liquid, weekly-listed ticker (SPY, QQQ, NVDA)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {results && Array.isArray(results) && results.length > 0 && (
        <div ref={resultsAnchorRef} data-tour="results-section" style={{ scrollMarginTop: '12px' }}>
          <ScreenerResponsive
            results={results}
            scenarioParams={lastRunParamSets}
            screenerMode="intraday"
            scenarioBasePrice={scenarioBasePrice}
            csvFileName="zerodte_heatmap_optionsLeveling.csv"
          />
        </div>
      )}

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}
    </div>
  );
}

export default ZeroDTEPage;
